import React, { useState } from 'react';
import { Company } from '../types';
import {
  StyledCard, CardTitle, FormGrid, StyledFormGroup,
  TooltipWrapper, InfoIcon, DropdownList, DropdownItem,
} from '../cardStyles';

interface Props {
  companies: Company[];
  selectedDestinatario: { company: Company } | null;
  onSelect: (value: { company: Company }) => void;
  readOnly?: boolean;
}

const LabelWithInfo = ({ label, tooltip }: { label: string; tooltip: string }) => (
  <div style={{ display: 'flex', alignItems: 'center' }}>
    <label style={{ marginBottom: 0 }}>{label}</label>
    <TooltipWrapper data-tooltip={tooltip}>
      <InfoIcon>i</InfoIcon>
    </TooltipWrapper>
  </div>
);

const getEnderecoFormatted = (company: Company | null | undefined): string => {
  if (!company) return '';
  const address = (company as any).Address || (company as any).address;
  if (!address) return '';

  const logradouro = address.street || '';
  const numero = address.number || '';
  const complemento = address.complement || '';
  const cepVal = address.cep
    ? String(address.cep).replace(/^(\d{5})(\d{3})$/, '$1-$2')
    : '';

  const neighborhood = address.Neighborhood || address.neighborhood;
  const city = neighborhood?.City || neighborhood?.city;
  const cityName = city?.name || '';
  const stateUf = city?.State?.uf || city?.state?.uf || '';

  const parts = [];
  if (logradouro) parts.push(logradouro);
  if (numero) parts.push(numero);
  if (complemento) parts.push(complemento);
  if (cepVal) parts.push(`CEP ${cepVal}`);

  const baseAddress = parts.join(', ');
  const cityState =
    cityName && stateUf ? `${cityName}/${stateUf}` : cityName || stateUf || '';

  if (baseAddress && cityState) return `${baseAddress} — ${cityState}`;
  return baseAddress || cityState;
};

const DestinatarioSection: React.FC<Props> = ({
  companies,
  selectedDestinatario,
  onSelect,
  readOnly = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');

  const company = selectedDestinatario?.company ?? null;

  const filtered = companies.filter((c) => {
    const q = search.toLowerCase();
    return (
      String(c.cnpj || '').toLowerCase().includes(q) ||
      String(c.companyName || '').toLowerCase().includes(q)
    );
  });

  const contribuinteLabel =
    (company as any)?.contribuinteIcms === 'CONTRIBUINTE'
      ? 'Contribuinte'
      : (company as any)?.contribuinteIcms === 'ISENTO'
      ? 'Isento'
      : (company as any)?.contribuinteIcms === 'NAO_CONTRIBUINTE'
      ? 'Não contribuinte'
      : '—';

  const inscricaoEstadual =
    (company as any)?.contribuinteIcms === 'CONTRIBUINTE'
      ? (company as any)?.stateRegistration || ''
      : '';

  return (
    <StyledCard>
      <CardTitle style={{ fontSize: '12px', color: '#999', textTransform: 'uppercase', marginBottom: '20px' }}>
        Destinatário
      </CardTitle>
      <FormGrid cols={2}>
        <StyledFormGroup>
          <LabelWithInfo label="CNPJ / CPF" tooltip="CNPJ (14 dígitos) ou CPF (11 dígitos) do destinatário" />
          {readOnly ? (
            <input type="text" readOnly value={company?.cnpj || '—'} />
          ) : (
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Pesquisar Destinatário..."
                autoComplete="off"
                value={isOpen ? search : (company ? company.cnpj : '')}
                onChange={(e) => { setSearch(e.target.value); setIsOpen(true); }}
                onFocus={() => { setSearch(''); setIsOpen(true); }}
                onBlur={() => setTimeout(() => setIsOpen(false), 200)}
                style={{ background: '#fff', textAlign: 'left' }}
              />
              {isOpen && filtered.length > 0 && (
                <DropdownList style={{ top: 'calc(100% + 4px)', zIndex: 9999, borderRadius: '8px' }}>
                  {filtered.map((c) => (
                    <DropdownItem
                      key={c.id}
                      onMouseDown={() => { onSelect({ company: c }); setIsOpen(false); }}
                    >
                      <span style={{ fontSize: '12px', color: '#333' }}>{c.cnpj} — {c.companyName}</span>
                    </DropdownItem>
                  ))}
                </DropdownList>
              )}
            </div>
          )}
        </StyledFormGroup>

        <StyledFormGroup>
          <LabelWithInfo label="Razão Social" tooltip="Nome completo ou Razão Social do destinatário" />
          <input type="text" placeholder="Nome do cliente" value={company?.companyName || ''} readOnly />
        </StyledFormGroup>

        <StyledFormGroup>
          <LabelWithInfo label="Contribuinte ICMS" tooltip="Status de contribuinte do destinatário" />
          <input type="text" readOnly value={contribuinteLabel} />
        </StyledFormGroup>

        <StyledFormGroup>
          <LabelWithInfo
            label="Inscrição Estadual"
            tooltip="Enviada apenas para destinatário contribuinte"
          />
          <input
            type="text"
            readOnly
            placeholder={(company as any)?.contribuinteIcms === 'CONTRIBUINTE' ? '' : 'Não aplicável'}
            value={inscricaoEstadual}
          />
        </StyledFormGroup>

        <div style={{
          gridColumn: 'span 2',
          display: 'grid',
          gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.7fr)',
          gap: '20px 30px',
          alignItems: 'start',
        }}>
          <StyledFormGroup>
            <LabelWithInfo label="E-mail" tooltip="E-mail para envio automático" />
            <input type="email" placeholder="nfe@cliente.com.br" value={(company as any)?.email || ''} readOnly />
          </StyledFormGroup>
          <StyledFormGroup>
            <LabelWithInfo label="Endereço" tooltip="Endereço completo do destinatário" />
            <input
              type="text"
              title={getEnderecoFormatted(company)}
              placeholder="Logradouro, número, complemento, CEP — Cidade/UF"
              value={getEnderecoFormatted(company)}
              readOnly
            />
          </StyledFormGroup>
        </div>
      </FormGrid>
    </StyledCard>
  );
};

export default DestinatarioSection;
